import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  File,
  FileText,
  Link2,
  Copy,
  Power,
  Trash2,
  ShieldCheck,
  BarChart3,
  Loader2,
  AlertTriangle,
  Plus,
  Download,
  Activity,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, uploadToS3 } from '../lib/api';
import type { DocumentMeta, Link as ShareLink } from '../lib/types';
import { timeAgo, viewerUrl, formatDate, fileSize } from '../lib/format';
import { ActivityList } from './ActivityList';

/** Shared upload pipeline: any file type is accepted. PDFs go through the
 *  render pipeline; everything else is hosted as a download-only file. */
function useUploader(projectId: string, onChange: () => void) {
  const [busy, setBusy] = useState(false);

  const uploadFiles = async (
    files: Iterable<File>,
    kind: 'doc' | 'nda' = 'doc',
    target: DocumentMeta | null = null,
  ) => {
    setBusy(true);
    try {
      for (const file of files) {
        const contentType = file.type || 'application/octet-stream';
        if (kind === 'nda' && contentType !== 'application/pdf') {
          toast.error('The NDA must be a PDF');
          continue;
        }
        if (target) {
          const { version, uploadUrl } = await api.createVersion(projectId, target.docId, {
            contentType,
            fileName: file.name,
            sizeBytes: file.size,
          });
          await uploadToS3(uploadUrl, file);
          await api.ingestVersion(projectId, target.docId, version.version);
          toast.success(`Uploading v${version.version} of ${target.title}`);
        } else {
          const title = file.name.replace(/\.[A-Za-z0-9]{1,10}$/, '');
          const { doc, uploadUrl } = await api.createDoc(projectId, {
            title,
            contentType,
            fileName: file.name,
            sizeBytes: file.size,
            kind,
          });
          await uploadToS3(uploadUrl, file);
          await api.ingestVersion(projectId, doc.docId, 1);
          toast.success(
            contentType === 'application/pdf' ? `Processing ${title}` : `Hosted ${file.name}`,
          );
        }
      }
      onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return { uploadFiles, busy };
}

export default function ProjectPage() {
  const { projectId = '' } = useParams();
  const qc = useQueryClient();
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const q = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId),
    refetchInterval: (query) =>
      query.state.data?.docs.some((d) =>
        d.versions?.some((v) => v.status === 'processing' || v.status === 'uploading'),
      )
        ? 3000
        : false,
  });
  const activity = useQuery({
    queryKey: ['projectVisits', projectId],
    queryFn: () => api.projectVisits(projectId),
    refetchInterval: 30_000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['project', projectId] });
    void qc.invalidateQueries({ queryKey: ['projectVisits', projectId] });
  };
  const { uploadFiles, busy } = useUploader(projectId, refresh);

  if (q.isLoading) return <p className="text-soft">Loading…</p>;
  if (!q.data) return <p className="text-soft">Project not found.</p>;
  const { project, docs, links, ndaSignatures } = q.data;
  const regularDocs = docs.filter((d) => d.kind === 'doc');
  const ndaDoc = docs.find((d) => d.kind === 'nda') ?? null;

  return (
    <div
      className="relative min-h-[70vh]"
      onDragEnter={(e) => {
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        dragDepth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
      onDragLeave={() => {
        dragDepth.current = Math.max(0, dragDepth.current - 1);
        if (dragDepth.current === 0) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        if (e.dataTransfer.files.length) void uploadFiles(e.dataTransfer.files);
      }}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center border-2 border-dashed border-accent bg-ink/85">
          <div className="text-center">
            <Upload className="mx-auto mb-3 h-8 w-8 text-accent" />
            <p className="rw-heading text-xs text-white">
              Drop files to add them to {project.name}
            </p>
            <p className="mt-2 text-xs text-soft">
              PDFs get page-by-page analytics; other files are hosted for download
            </p>
          </div>
        </div>
      )}

      <div className="mb-8 flex items-center gap-3">
        <Link to="/" className="rw-heading text-xs text-soft hover:text-white">
          Projects
        </Link>
        <span className="text-mid">/</span>
        <h1 className="rw-heading text-sm text-white">{project.name}</h1>
      </div>

      <div className="grid gap-8 lg:grid-cols-2">
        <DocsPanel
          projectId={projectId}
          docs={regularDocs}
          ndaDoc={ndaDoc}
          onChange={refresh}
          uploadFiles={uploadFiles}
          busy={busy}
        />
        <LinksPanel
          projectId={projectId}
          links={links}
          docs={regularDocs}
          hasNda={!!ndaDoc && ndaDoc.currentVersion > 0}
          onChange={refresh}
        />
      </div>

      {/* ── Activity across every link in this project ── */}
      <section className="mt-12">
        <h2 className="rw-heading mb-4 flex items-center gap-2 text-sm text-white">
          <Activity className="h-4 w-4 text-accent" /> Activity
        </h2>
        <ActivityList
          visits={activity.data?.visits ?? []}
          raw={activity.data?.raw ?? []}
          docTitles={activity.data?.docTitles ?? {}}
          showLink
          emptyText="Every visit to this project's links will appear here — who opened it, how long they read, page by page."
        />
      </section>

      {ndaSignatures.length > 0 && (
        <section className="mt-12">
          <h2 className="rw-heading mb-4 flex items-center gap-2 text-sm text-white">
            <ShieldCheck className="h-4 w-4 text-accent" /> NDA signatures
          </h2>
          <div className="rw-card overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-paper text-[11px] uppercase tracking-wider text-mid">
                <tr>
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold">Email</th>
                  <th className="px-3 py-2 font-semibold">Signed</th>
                  <th className="px-3 py-2 font-semibold">Version</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {ndaSignatures.map((s) => (
                  <tr key={s.signatureId} className="border-t border-line/40">
                    <td className="px-3 py-2 text-ink">{s.name}</td>
                    <td className="px-3 py-2 text-mid">{s.email}</td>
                    <td className="px-3 py-2 text-mid">{formatDate(s.signedAt)}</td>
                    <td className="px-3 py-2 text-mid">v{s.ndaVersion}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={async () => {
                          const { url } = await api.ndaDownload(projectId, s.signatureId);
                          window.open(url, '_blank');
                        }}
                        className="p-1 text-mid hover:text-accent"
                        title="Download signed PDF"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ──────── Documents panel ────────

function DocsPanel({
  projectId,
  docs,
  ndaDoc,
  onChange,
  uploadFiles,
  busy,
}: {
  projectId: string;
  docs: DocumentMeta[];
  ndaDoc: DocumentMeta | null;
  onChange: () => void;
  uploadFiles: (
    files: Iterable<File>,
    kind?: 'doc' | 'nda',
    target?: DocumentMeta | null,
  ) => Promise<void>;
  busy: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const pickRef = useRef<{ kind: 'doc' | 'nda'; target: DocumentMeta | null }>({
    kind: 'doc',
    target: null,
  });

  const pickFile = (kind: 'doc' | 'nda', target: DocumentMeta | null = null) => {
    pickRef.current = { kind, target };
    if (fileRef.current) {
      fileRef.current.accept =
        kind === 'nda' || target?.kind === 'nda' || (target && !target.downloadOnly)
          ? 'application/pdf'
          : '';
      fileRef.current.multiple = !target;
    }
    fileRef.current?.click();
  };

  return (
    <section>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const files = e.target.files;
          if (files?.length) {
            void uploadFiles([...files], pickRef.current.kind, pickRef.current.target).then(
              () => {
                if (fileRef.current) fileRef.current.value = '';
              },
            );
          }
        }}
      />
      <div className="mb-4 flex items-center justify-between">
        <h2 className="rw-heading flex items-center gap-2 text-xs text-white">
          <FileText className="h-4 w-4 text-accent" /> Documents
        </h2>
        <div className="flex gap-2">
          <button disabled={busy} onClick={() => pickFile('doc')} className="rw-btn-ghost">
            <Upload className="h-3.5 w-3.5" /> Upload files
          </button>
          {!ndaDoc && (
            <button disabled={busy} onClick={() => pickFile('nda')} className="rw-btn-ghost">
              <ShieldCheck className="h-3.5 w-3.5" /> Set NDA
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {[...docs, ...(ndaDoc ? [ndaDoc] : [])].map((d) => (
          <DocRow
            key={d.docId}
            projectId={projectId}
            doc={d}
            onNewVersion={() => pickFile(d.kind, d)}
            onChange={onChange}
          />
        ))}
        {docs.length === 0 && !ndaDoc && (
          <p className="border border-dashed border-white/20 p-8 text-center text-sm text-soft">
            Drag & drop files anywhere on this page — PDFs get page-by-page
            analytics, everything else is hosted for download.
          </p>
        )}
      </div>
    </section>
  );
}

function DocRow({
  projectId,
  doc,
  onNewVersion,
  onChange,
}: {
  projectId: string;
  doc: DocumentMeta;
  onNewVersion: () => void;
  onChange: () => void;
}) {
  const latest = doc.versions?.find((v) => v.version === doc.latestVersion);
  const status = latest?.status ?? 'ready';

  const del = async () => {
    if (!confirm(`Delete "${doc.title}"? Links will stop showing it.`)) return;
    await api.deleteDoc(projectId, doc.docId);
    onChange();
  };

  return (
    <div className="rw-card p-3">
      <div className="flex items-center gap-2">
        {doc.kind === 'nda' ? (
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600" />
        ) : doc.downloadOnly ? (
          <File className="h-4 w-4 shrink-0 text-accent" />
        ) : (
          <FileText className="h-4 w-4 shrink-0 text-mid" />
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
          {doc.title}
          {doc.kind === 'nda' && (
            <span className="ml-2 text-xs font-normal text-emerald-600">project NDA</span>
          )}
          {doc.downloadOnly && (
            <span className="ml-2 text-xs font-normal text-accent">download file</span>
          )}
        </span>
        {status === 'processing' || status === 'uploading' ? (
          <span className="flex items-center gap-1.5 text-xs text-accent">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {latest?.pageCount
              ? `${latest.pagesRendered}/${latest.pageCount} pages`
              : 'processing'}
          </span>
        ) : status === 'failed' ? (
          <span
            className="flex items-center gap-1 text-xs text-accent"
            title={latest?.error ?? undefined}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> failed
          </span>
        ) : (
          <span className="text-xs text-mid">
            v{doc.currentVersion} ·{' '}
            {doc.downloadOnly ? fileSize(latest?.sizeBytes) : `${latest?.pageCount ?? '?'} pages`}
          </span>
        )}
        {doc.kind === 'doc' && !doc.downloadOnly && (
          <Link
            to={`/p/${projectId}/docs/${doc.docId}`}
            className="p-1 text-mid hover:text-accent"
            title="Analytics"
          >
            <BarChart3 className="h-4 w-4" />
          </Link>
        )}
        <button onClick={onNewVersion} className="p-1 text-mid hover:text-accent" title="Upload new version">
          <Upload className="h-4 w-4" />
        </button>
        <button onClick={del} className="p-1 text-soft hover:text-accent" title="Delete">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ──────── Links panel ────────

function LinksPanel({
  projectId,
  links,
  docs,
  hasNda,
  onChange,
}: {
  projectId: string;
  links: ShareLink[];
  docs: DocumentMeta[];
  hasNda: boolean;
  onChange: () => void;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="rw-heading flex items-center gap-2 text-xs text-white">
          <Link2 className="h-4 w-4 text-accent" /> Investor links
        </h2>
        <button onClick={() => setCreating(true)} className="rw-btn">
          <Plus className="h-3.5 w-3.5" /> New link
        </button>
      </div>

      {creating && (
        <LinkForm
          projectId={projectId}
          docs={docs}
          hasNda={hasNda}
          onDone={() => {
            setCreating(false);
            onChange();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <div className="space-y-2">
        {links.map((l) => (
          <LinkRow key={l.linkId} projectId={projectId} link={l} onChange={onChange} />
        ))}
        {links.length === 0 && !creating && (
          <p className="border border-dashed border-white/20 p-8 text-center text-sm text-soft">
            Create one unique link per investor so visits are attributable.
          </p>
        )}
      </div>
    </section>
  );
}

function LinkForm({
  projectId,
  docs,
  hasNda,
  onDone,
  onCancel,
}: {
  projectId: string;
  docs: DocumentMeta[];
  hasNda: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const readyDocs = docs.filter((d) => d.currentVersion > 0);
  const [label, setLabel] = useState('');
  const [docIds, setDocIds] = useState<string[]>(readyDocs.map((d) => d.docId));
  const [requireEmail, setRequireEmail] = useState(true);
  const [requireNda, setRequireNda] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.createLink(projectId, { label, docIds, requireEmail, requireNda, allowDownload }),
    onSuccess: ({ link }) => {
      void navigator.clipboard.writeText(viewerUrl(link.slug));
      toast.success('Link created and copied to clipboard');
      onDone();
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
      className="rw-card mb-3 p-4"
    >
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (internal), e.g. Sequoia – Jane"
        className="w-full border border-line bg-white px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
      />
      <div className="mt-3 text-[11px] font-semibold uppercase tracking-wider text-mid">
        Visible documents
      </div>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {readyDocs.map((d) => (
          <label
            key={d.docId}
            className={`cursor-pointer border px-2.5 py-1 text-xs ${
              docIds.includes(d.docId)
                ? 'border-accent bg-accent/10 font-medium text-accent'
                : 'border-line text-mid'
            }`}
          >
            <input
              type="checkbox"
              className="hidden"
              checked={docIds.includes(d.docId)}
              onChange={(e) =>
                setDocIds((prev) =>
                  e.target.checked
                    ? [...prev, d.docId]
                    : prev.filter((x) => x !== d.docId),
                )
              }
            />
            {d.title}
          </label>
        ))}
        {readyDocs.length === 0 && (
          <span className="text-xs text-mid">No processed documents yet.</span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-charcoal">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={requireEmail || requireNda}
            disabled={requireNda}
            onChange={(e) => setRequireEmail(e.target.checked)}
            className="accent-accent"
          />
          Require email
        </label>
        <label
          className={`flex items-center gap-1.5 ${!hasNda ? 'opacity-40' : ''}`}
          title={hasNda ? undefined : 'Upload a project NDA first'}
        >
          <input
            type="checkbox"
            disabled={!hasNda}
            checked={requireNda}
            onChange={(e) => setRequireNda(e.target.checked)}
            className="accent-accent"
          />
          Require NDA
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={allowDownload}
            onChange={(e) => setAllowDownload(e.target.checked)}
            className="accent-accent"
          />
          Allow downloads
        </label>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          disabled={!label.trim() || docIds.length === 0 || create.isPending}
          className="rw-btn"
        >
          Create link
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 text-xs text-mid hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function LinkRow({
  projectId,
  link,
  onChange,
}: {
  projectId: string;
  link: ShareLink;
  onChange: () => void;
}) {
  const toggle = async () => {
    await api.patchLink(projectId, link.linkId, { active: !link.active });
    toast.success(link.active ? 'Link disabled' : 'Link re-enabled');
    onChange();
  };
  const del = async () => {
    if (!confirm(`Delete link "${link.label}"? Its URL stops working immediately.`)) return;
    await api.deleteLink(projectId, link.linkId);
    onChange();
  };
  const copy = () => {
    void navigator.clipboard.writeText(viewerUrl(link.slug));
    toast.success('Copied');
  };

  return (
    <div className={`rw-card p-3 ${link.active ? '' : 'opacity-60'}`}>
      <div className="flex items-center gap-2">
        <Link
          to={`/p/${projectId}/links/${link.linkId}`}
          className="min-w-0 flex-1 truncate text-sm font-medium text-ink hover:text-accent"
        >
          {link.label}
        </Link>
        <span className="text-xs tabular-nums text-mid">
          {link.visitCount ?? 0} visits
          {link.lastVisitAt ? ` · ${timeAgo(link.lastVisitAt)}` : ''}
        </span>
        <button onClick={copy} title="Copy URL" className="p-1 text-mid hover:text-accent">
          <Copy className="h-4 w-4" />
        </button>
        <button
          onClick={toggle}
          title={link.active ? 'Disable (kill switch)' : 'Enable'}
          className={`p-1 hover:text-accent ${link.active ? 'text-emerald-600' : 'text-soft'}`}
        >
          <Power className="h-4 w-4" />
        </button>
        <button onClick={del} title="Delete" className="p-1 text-soft hover:text-accent">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-mid">
        <code className="bg-paper px-1.5 py-0.5">/v/{link.slug}</code>
        <span>{link.docIds.length} docs</span>
        {link.requireEmail && <span className="text-charcoal">email</span>}
        {link.requireNda && <span className="font-medium text-emerald-600">NDA</span>}
        {link.allowDownload && <span className="text-charcoal">downloads</span>}
        {!link.active && <span className="font-medium text-accent">disabled</span>}
      </div>
    </div>
  );
}
